import type { JSX } from "react"
import { DateTime } from "luxon"
import fp from "lodash/fp"

import type { TFunction } from "i18next"
import type { MarkElementProps, ScatterMarkerProps } from "@mui/x-charts"
import type { DatasetType } from "@mui/x-charts/internals"
import type { Palette } from "@mui/material"

import type {
  Axis,
  RecordWTimestamp,
  LineViewProperties,
  RecordType,
  ScatterViewProperties,
} from "types/dashboard.types"
import type { Mark } from "types/viewProperties.types"
import type { FieldQualityMap, QualityLevel } from "types/quality.types"

import { customValueFormatter } from "helpers/formatters/plots/plots"
import { getTelemetryTranslation } from "helpers/utils/translations"
import { arrayRange } from "helpers/utils/common"
import { FilledCircleMark } from "../mui/MuiMarks"

const DEFAULT_TIME_FORMAT = "YYYY-MM-DD HH:mm"
const VIS_SIG_DIGITS = 3

interface FormatterNumberProps {
  type: "number"
  prefix?: string
  suffix?: string
  base?: string
  trimZeros?: boolean
  decimalPlaces?: number
  formatUnits?: boolean
  useScientificNotation?: boolean
  locale?: string
}
const defaultFormatterNumberProps = {
  trimZeros: true,
  decimalPlaces: VIS_SIG_DIGITS,
  formatUnits: true,
}

interface FormatterTimeProps {
  type: "time"
  timeFormat?: string
}
const defaultFormatterTimeProps = {
  timeFormat: DEFAULT_TIME_FORMAT,
}

type FormatterProps = FormatterNumberProps | FormatterTimeProps
type Formatter =
  | ((value: number) => string)
  | ((value: Date) => string)
  | ((value: any) => string)

export const getFormatter = (props: FormatterProps): Formatter => {
  if (props.type === "number") {
    const {
      base,
      prefix,
      suffix,
      decimalPlaces,
      formatUnits,
      locale,
      trimZeros,
      useScientificNotation,
    } = {
      ...defaultFormatterNumberProps,
      ...props,
    }
    if (base === "10" || base === "") {
      return customValueFormatter({
        prefix,
        suffix,
        decimalPlaces,
        formatUnits,
        locale,
        trimZeros,
        useScientificNotation,
      })
    }
  }

  if (props.type === "time") {
    const { timeFormat } = {
      ...defaultFormatterTimeProps,
      ...props,
    }
    return (value: Date) => DateTime.fromJSDate(value).toFormat(timeFormat)
  }

  return (_: any) => ""
}

export const axisToFormatterProps = (
  axis: Axis,
  properties: LineViewProperties | ScatterViewProperties,
  locale?: string,
): FormatterProps => {
  if (axis.type === "number") {
    return {
      type: axis.type,
      prefix: axis.prefix,
      suffix: axis.suffix,
      base: axis.base,
      decimalPlaces: axis.decimalPlaces,
      formatUnits: axis.formatUnits,
      useScientificNotation: axis.useScientificNotation,
      locale,
    }
  } else if (axis.type === "time") {
    return {
      type: axis.type,
      timeFormat: properties.timeFormat,
    }
  } else {
    throw TypeError("Unknown axis type")
  }
}

export const getSeriesLabel = (record: Record<string, string>, t: TFunction): string =>
  fp.flow(
    fp.toPairs,
    fp.map(([key, value]) => {
      return key === "_field"
        ? getTelemetryTranslation(value, t)
        : typeof value == "string"
          ? t(`plots_tooltip.${value.toUpperCase()}`, { defaultValue: value })
          : value
    }),
    fp.join(" - "),
  )(record)

export const getSeriesKey = (item: RecordType, fill: string[]): string => {
  const picked = fp.pick(fill, item)
  return JSON.stringify(picked)
}

export const buildSeriesByKey = (
  formattedPlotData: RecordWTimestamp[],
  fill: string[],
): Record<string, Record<string, string>> => {
  return fp.flow(
    fp.map((d: RecordWTimestamp) => {
      const key = getSeriesKey(d, fill)
      const picked = JSON.parse(key)
      return [key, picked]
    }),
    fp.uniqBy(([key]) => key),
    fp.fromPairs,
  )(formattedPlotData)
}

type ColorMap = Record<string, string>

export const buildSeriesColorMap = (
  lines: Record<string, Record<string, string>>,
  fallbackColors: string[],
  fieldProperties?: Record<
    string,
    { color?: string; curve?: string; backgroundShading?: boolean }
  >,
): ColorMap => {
  const allKeys = Object.keys(lines).sort()

  // get the colors assigned in fieldProperties
  const preassignedColorMap: ColorMap = fp.flow(
    // split by field properties as an array of [key, values]
    fp.entries,
    // filter out the ones with no color, and transform the pair to [key, color]
    fp.flatMap(([k, { color }]) => (color ? [[k, color]] : [])),
    fp.fromPairs,
  )(fieldProperties || {})

  // assign color to the rest of the lines
  const other: ColorMap = fp.flow(
    // ignore already assigned
    fp.filter((k: string) => !(k in preassignedColorMap)),
    // add an index to each key -> [key, i]
    (keys: string[]) => fp.zip(keys, fp.range(0, keys.length)),
    // convert the index to a color
    fp.map(([key, index]: [string, number]) => [
      key,
      fallbackColors[index % fallbackColors.length],
    ]),
    fp.fromPairs,
  )(allKeys)

  return { ...preassignedColorMap, ...other }
}

export const splitFormatter = (formatter: Formatter): Formatter => {
  return (v: any) => formatter(v).replace(" ", "\n")
}

export const getMark = (mark: Mark): ((props: MarkElementProps) => JSX.Element) => {
  switch (mark) {
    case "filledCircle":
    default:
      return FilledCircleMark
  }
}

export const getFieldFromSeriesId = (seriesId: string): string | undefined => {
  try {
    const parsed = JSON.parse(seriesId)
    return parsed._field
  } catch {
    return undefined
  }
}

export const createQualityLineMark = (
  fieldQualityMap: FieldQualityMap,
  formattedData: DatasetType,
  qualityColors: Record<string, string>,
) => {
  const QualityLineMark = (props: MarkElementProps) => {
    const { x, y, color, dataIndex, id, isHighlighted } = props

    const field = getFieldFromSeriesId(id as string)
    const row = formattedData[dataIndex]
    const timeMillis =
      row?._time instanceof Date ? DateTime.fromJSDate(row._time).toMillis() : null

    const qualityInfo =
      field && timeMillis ? fieldQualityMap[timeMillis]?.[field] : undefined

    const hasQualityIssue = qualityInfo?.quality && qualityInfo.quality !== "healthy"

    if (hasQualityIssue) {
      const strokeColor = qualityColors[qualityInfo!.quality!] ?? color
      const outerRadius = 4.5
      const innerRadius = isHighlighted ? 3.75 : 2.5
      const strokeWidth = isHighlighted ? 3 : 1.5
      return (
        <g>
          <circle
            cx={x}
            cy={y}
            r={outerRadius}
            fill="none"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
          />
          <circle cx={x} cy={y} r={innerRadius} fill={color} />
        </g>
      )
    }

    const normalRadius = isHighlighted ? 5 : 2.5
    return <circle cx={x} cy={y} r={normalRadius} fill={color} />
  }

  return QualityLineMark
}

export const createQualityScatterMarker = (
  fieldQualityMap: FieldQualityMap,
  formattedData: Record<string, Array<{ x: unknown }>>,
  qualityColors: Record<string, string>,
) => {
  const QualityScatterMarker = (props: ScatterMarkerProps) => {
    const { x, y, color, dataIndex, seriesId, isHighlighted, isFaded, size, onClick } =
      props

    const field = getFieldFromSeriesId(seriesId as string)
    const rawX = field ? formattedData[field]?.[dataIndex]?.x : null
    const timeMillis = rawX instanceof Date ? rawX.getTime() : null

    const qualityInfo =
      field && timeMillis ? fieldQualityMap[timeMillis]?.[field] : undefined
    const hasQualityIssue = qualityInfo?.quality && qualityInfo.quality !== "healthy"

    if (hasQualityIssue) {
      const strokeColor = qualityColors[qualityInfo!.quality!] ?? color
      const outerRadius = 4.5
      const innerRadius = isHighlighted ? 3.75 : 2.5
      const strokeWidth = isHighlighted ? 3 : 1.5
      return (
        <g onClick={onClick}>
          <circle
            cx={x}
            cy={y}
            r={outerRadius}
            fill="none"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            opacity={isFaded ? 0.3 : 1}
          />
          <circle
            cx={x}
            cy={y}
            r={innerRadius}
            fill={color}
            opacity={isFaded ? 0.3 : 1}
          />
        </g>
      )
    }

    const r = isHighlighted ? size * 1.5 : size
    return (
      <circle
        cx={x}
        cy={y}
        r={r}
        fill={color}
        opacity={isFaded ? 0.3 : 1}
        onClick={onClick}
      />
    )
  }

  return QualityScatterMarker
}

// Generate explicit tick values for an axis and ensures bounds (if specified in the def) are always included in the ticks
export const generateAxisTicks = (
  min: number,
  max: number,
  totalTicks?: number,
  tickInterval?: number,
): number[] | undefined => {
  if (tickInterval) {
    const count = Math.floor((max - min) / tickInterval) + 1
    const ticks = fp.times((i) => min + i * tickInterval, count)
    // Ensure max is included if it wasn't hit exactly
    return fp.last(ticks) !== max ? [...ticks, max] : ticks
  }

  if (totalTicks && totalTicks >= 2) {
    // Generate evenly spaced ticks including bounds
    const step = (max - min) / (totalTicks - 1)
    return arrayRange(min, max, step)
  }

  return undefined
}

export const buildTickConfig = (
  tickValues: number[] | Date[] | undefined,
  totalTicks: number | undefined,
  tickStep: number | undefined,
  domainRange?: number,
): Record<string, number | number[] | Date[]> =>
  tickValues
    ? { tickInterval: tickValues }
    : totalTicks && domainRange
      ? { tickMinStep: domainRange / totalTicks }
      : totalTicks
        ? { tickNumber: totalTicks }
        : tickStep
          ? { tickMinStep: tickStep }
          : {}

// Build a timestamp-to-value map for a specific field in the formatted plot data
export const buildValueMap = (
  formattedPlotData: RecordWTimestamp[],
  fieldKey: string,
  fill: string[],
): Map<number, number> => {
  const map = new Map<number, number>()
  formattedPlotData.forEach((item: RecordWTimestamp) => {
    const key = getSeriesKey(item, fill)
    if (key === fieldKey && typeof item._value === "number") {
      map.set((item._time as Date).getTime(), item._value)
    }
  })
  return map
}

// Monotone cubic interpolation (matches monotoneX curve)
// Uses Fritsch-Carlson method to ensure smooth, monotonic interpolation
export const interpolateMonotone = (
  timeMs: number,
  valueMap: Map<number, number>,
): number | null => {
  // If we have the exact value, return it
  const exactValue = valueMap.get(timeMs)
  if (exactValue !== undefined) {
    return exactValue
  }

  // Get sorted timestamps and values
  const timestamps = Array.from(valueMap.keys()).sort((a, b) => a - b)
  const values = timestamps.map((t) => valueMap.get(t)!)

  if (timestamps.length < 2) {
    return null
  }

  // Find the segment containing timeMs
  let segmentIdx = -1
  for (let i = 0; i < timestamps.length - 1; i++) {
    if (timeMs >= timestamps[i] && timeMs <= timestamps[i + 1]) {
      segmentIdx = i
      break
    }
  }

  if (segmentIdx === -1) {
    return null
  }

  // Calculate tangents using Fritsch-Carlson method for monotone interpolation
  const tangents = new Array(timestamps.length)

  // Calculate secants (slopes between consecutive points)
  const secants = new Array(timestamps.length - 1)
  for (let i = 0; i < timestamps.length - 1; i++) {
    const dx = timestamps[i + 1] - timestamps[i]
    const dy = values[i + 1] - values[i]
    secants[i] = dy / dx
  }

  // Initialize tangents
  tangents[0] = secants[0]
  for (let i = 1; i < timestamps.length - 1; i++) {
    // If secants have different signs or one is zero, use zero tangent (flat)
    if (secants[i - 1] * secants[i] <= 0) {
      tangents[i] = 0
    } else {
      // Weighted harmonic mean for smooth monotone interpolation
      tangents[i] = (secants[i - 1] + secants[i]) / 2
    }
  }
  tangents[timestamps.length - 1] = secants[secants.length - 1]

  // Apply monotonicity constraints (Fritsch-Carlson)
  for (let i = 0; i < timestamps.length - 1; i++) {
    if (Math.abs(secants[i]) < 1e-10) {
      // Horizontal segment
      tangents[i] = 0
      tangents[i + 1] = 0
    } else {
      // Check and constrain tangents to maintain monotonicity
      const alpha = tangents[i] / secants[i]
      const beta = tangents[i + 1] / secants[i]
      const tau = 3 / Math.sqrt(alpha * alpha + beta * beta)

      if (tau < 1) {
        tangents[i] = tau * alpha * secants[i]
        tangents[i + 1] = tau * beta * secants[i]
      }
    }
  }

  // Perform cubic Hermite interpolation
  const x0 = timestamps[segmentIdx]
  const x1 = timestamps[segmentIdx + 1]
  const y0 = values[segmentIdx]
  const y1 = values[segmentIdx + 1]
  const m0 = tangents[segmentIdx]
  const m1 = tangents[segmentIdx + 1]

  const dx = x1 - x0
  const t = (timeMs - x0) / dx
  const t2 = t * t
  const t3 = t2 * t

  // Hermite basis functions
  const h00 = 2 * t3 - 3 * t2 + 1
  const h10 = t3 - 2 * t2 + t
  const h01 = -2 * t3 + 3 * t2
  const h11 = t3 - t2

  return h00 * y0 + h10 * dx * m0 + h01 * y1 + h11 * dx * m1
}

// Build value maps for all line fills
export const buildLineFillValueMaps = (
  lineFills: any[],
  seriesByKey: Record<string, Record<string, string>>,
  formattedPlotData: RecordWTimestamp[],
  fill: string[],
): Map<number, Map<number, number>>[] => {
  return lineFills.map((lineFill: any) => {
    const keyA = fp.find(
      (key: string) => seriesByKey[key]?._field === lineFill.lineA,
      fp.keys(seriesByKey),
    )
    const keyB = fp.find(
      (key: string) => seriesByKey[key]?._field === lineFill.lineB,
      fp.keys(seriesByKey),
    )

    return new Map([
      [1, keyA ? buildValueMap(formattedPlotData, keyA, fill) : new Map()],
      [2, keyB ? buildValueMap(formattedPlotData, keyB, fill) : new Map()],
    ])
  })
}

// Calculate line fill data for stacked area between two lines
export const calculateLineFillData = (
  lineFills: any[],
  seriesByKey: Record<string, Record<string, string>>,
  fieldsAtTime: Record<string, any>,
  time: Date | string,
  lineFillValueMaps: Map<number, Map<number, number>>[],
): Record<string, number | null> => {
  return lineFills.reduce(
    (acc: Record<string, number | null>, lineFill: any, index: number) => {
      const keyA = fp.find(
        (key: string) => seriesByKey[key]?._field === lineFill.lineA,
        fp.keys(seriesByKey),
      )
      const keyB = fp.find(
        (key: string) => seriesByKey[key]?._field === lineFill.lineB,
        fp.keys(seriesByKey),
      )

      if (keyA && keyB) {
        const fillKey = `fill_${index}`
        const rawValueA = fieldsAtTime[keyA]
        const rawValueB = fieldsAtTime[keyB]
        const timeMs = (time as Date).getTime()

        const valueMapA = lineFillValueMaps[index].get(1)!
        const valueMapB = lineFillValueMaps[index].get(2)!

        // Get or interpolate values
        const valueA =
          typeof rawValueA === "number"
            ? rawValueA
            : interpolateMonotone(timeMs, valueMapA)

        const valueB =
          typeof rawValueB === "number"
            ? rawValueB
            : interpolateMonotone(timeMs, valueMapB)

        // Calculate min/max to support line crossings
        let baseValue: number | null = null
        let diffValue: number | null = null

        if (valueA !== null && valueB !== null) {
          const minValue = Math.min(valueA, valueB)
          const maxValue = Math.max(valueA, valueB)
          baseValue = minValue
          diffValue = maxValue - minValue
        }

        acc[`${fillKey}_base`] = baseValue
        acc[`${fillKey}_diff`] = diffValue
      }
      return acc
    },
    {} as Record<string, number | null>,
  )
}

export const getQualityColors = (
  palette: Palette,
): { [key in QualityLevel]: string } => ({
  healthy: palette.quality.healthy.main,
  degraded: palette.quality.degraded.main,
  unreliable: palette.quality.unreliable.main,
  invalid: palette.quality.invalid.main,
})
